import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DownArrow } from '../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../assets/images/uparrowcolored.svg';
import { ReactComponent as DeleteIcon } from '../../assets/images/deleteAlertIcon.svg';
import { ReactComponent as AddIcon } from '../../assets/images/CircleAdd.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import InformationModal from './InformationModal';
import HealthEligibilitySummaryData from './IFSP/HealthEligibilitySummary.json';
import {
  AlertColor,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import SimpleTextAndTextArea from '../../components/forms/SimpleTextAndTextArea';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import EvaluationTableRow from './EvaluationTableRow';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import IfspContext from './ifspContextApi/IfspContex';
import ModalPopup from '../../components/formlib/ModalPopup';
import { EmployeeListType } from '../../services/ifspClient/IfspApi';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import { OptionType } from '../../utils/type';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { formatName } from '../../utils/nameUtils';
import LazyAutocompleteSearch from '../../components/formlib/LazyAutocompleteSearch';
import { getAllEmployeeByName } from '../../services/configApi/employees/employeeServices';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';

interface HealthEligibilitySummaryProps {
  close: boolean;
  handelClose: Dispatch<SetStateAction<boolean>>;
  info: boolean;
  closeInfo: (info: boolean) => void;
  openInfo: (info: boolean) => void;
  selectedLanguage: string;
  hasEditPermission: boolean;
}

type EvalutaionDataType = {
  evaluationId: string;
  evaluationName: string;
  evaluationDiscipline: string;
};

interface TableDataType {
  evaluationName: string;
  evaluationDiscipline: string;
  action: string;
}

type MainHESDataType = {
  dateOfEvaluation: string | null | Dayjs;
  teamMember: {
    name: string;
    memberName: string;
    discipline: string;
  }[];
  evaluationResults: string;
  healthHistory: string;
  currentHealthStatus: string;
  additionalInfo: string;
  sessionReviewedDate: string | null | Dayjs;
};

const headCells: HeadCell<TableDataType>[] = [
  {
    id: 'evaluationName',
    labelId: 'Evaluation Name',
    defaultLabelId: 'Evaluation Team Member Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'evaluationDiscipline',
    labelId: 'Evaluation Discipline',
    defaultLabelId: 'Evaluation Team Member Discipline',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'action',
    labelId: 'Action',
    defaultLabelId: 'Action',
    numeric: false,
    requiredSorting: false,
  },
];

const validationSchema = yup.object().shape({
  evaluationName: yup.string().required('IFSP.EvaluationName'),
  evaluationDecipline: yup.string().required('IFSP.EvaluationDiscipline'),
});

const HealthEligibilitySummary: FC<HealthEligibilitySummaryProps> = ({
  close,
  handelClose,
  info,
  openInfo,
  closeInfo,
  selectedLanguage,
  hasEditPermission,
}) => {
  const [hesServiceQuestion, setHESServiceQuestion] = useState<
    { commentId: number; comments: string }[]
  >(
    HealthEligibilitySummaryData.Questions.map((_, index) => ({
      commentId: index,
      comments: '',
    })),
  );
  const [evaluvationData, setEvaluvationData] = useState<EvalutaionDataType[]>(
    [],
  );

  const {
    ifsp,
    initialIfspForReports,
    updateIfspContext,
    updateIfspForReport,
  } = useContext(IfspContext);
  const [newEvaluvatedName, setNewEvaluvatedName] = useState('');
  const [newEvaluvatedDiscipline, setNewEvaluvatedDiscipline] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [editEvaluationId, setEditEvaluationId] = useState<string>('');
  const [mainHesData, setMainHesData] = useState<MainHESDataType[]>(
    ifsp.ifspTeam.forms.healthSummary,
  );
  const [disciplineList, setDisciplineList] = useState<OptionType[]>([
    {
      id: '',
      label: '',
    },
  ]);
  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([]);
  const [deleteIndexId, setDeleteIndexId] = useState<string>('');
  const [deleteWarning, setDeleteWarning] = useState<boolean>(false);
  const [deleteNewSection, setDeleteNewSection] = useState<boolean>(false);
  const [openAccordianIndex, setOpenAccordianIndex] = useState<number>(0);
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [
    duplicateEvaluationDisciplineError,
    setDuplicateEvaluationDisciplineError,
  ] = useState<boolean>(false);
  const [selectedEvalName, setSelectedEvalName] = useState<string>('');
  const [toastrId, setToastrId] = useState('');
  const parentRef = useRef<HTMLDivElement>(null);
  const { lookups } = useContext(LookUpContext);
  const permission = hasEditPermission && selectedLanguage === 'en';
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  useEffect(() => {
    if (!permission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [permission]);

  const formikRef = useRef<
    FormikProps<{
      evaluationName: string;
      evaluationDecipline: string;
    }>
  >(null);

  const sanitizeSearch = (text: string) => {
    if (!text) return '';

    if (text.includes(',')) {
      const [last, first] = text.split(',');
      return `${first?.trim()} ${last?.trim()}`;
    }

    return text;
  };

  const fetchEmployees = (
    search: string,
    page: number,
  ): Promise<OptionType[]> => {
    const cleanSearch = sanitizeSearch(search);
    const healthSummaryEmployeeIds = ifsp.ifspTeam.forms.healthSummary[
      openAccordianIndex
    ].teamMember.map((y) => y.name);
    return getAllEmployeeByName(
      cleanSearch,
      page,
      10,
      page === 0 ? healthSummaryEmployeeIds : [],
    )
      .then((data) => {
        toggleLoader(false);
        setEmployeeList((prev) => (page === 0 ? data : [...prev, ...data]));
        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((err) => {
        toggleLoader(false);
        setToastrVariable('error');
        setToastrId('IFSP.failedToGetEmployeeData');
        setToastrDefaultMessage(err);

        return [];
      });
  };

  const evaluationMember = useMemo(() => {
    const found = employeeList.find((emp) => emp.id === selectedEvalName);

    if (!found) return null;

    return {
      id: found.id,
      label: `${found.lastName}, ${found.firstName}`,
    };
  }, [employeeList, selectedEvalName]);

  const addEvaluationdata = (values: {
    evaluationName: string;
    evaluationDecipline: string;
  }) => {
    const isDuplicated = evaluvationData.some((evaluation) => {
      return (
        evaluation.evaluationName === values.evaluationName &&
        evaluation.evaluationDiscipline === values.evaluationDecipline
      );
    });
    if (isDuplicated) {
      setDuplicateEvaluationDisciplineError(true);
      return;
    }
    if (isEdit) {
      const update = mainHesData[openAccordianIndex].teamMember.map((member) =>
        member.name === editEvaluationId
          ? {
              ...member,
              name: values.evaluationName,
              discipline: values.evaluationDecipline ?? '',
              memberName:
                employeeList.find((emp) => emp.id === values.evaluationName)
                  ?.name ?? '',
            }
          : member,
      );
      setMainHesData((prev) => {
        return prev.map((data, index) =>
          index === openAccordianIndex
            ? {
                ...data,
                teamMember: update,
              }
            : data,
        );
      });
      const updatedEvaluationData = evaluvationData.map((evaluation) =>
        evaluation.evaluationId === editEvaluationId
          ? {
              ...evaluation,
              evaluationName: values.evaluationName,
              evaluationDiscipline:
                employeeList
                  .find((emp) => emp.id === values.evaluationName)
                  ?.disciplineHistoryList?.find(
                    (history) =>
                      history.disciplineType === values.evaluationDecipline,
                  )?.disciplineType ?? '',
            }
          : evaluation,
      );
      setEvaluvationData(updatedEvaluationData);
      setIsEdit(false);
    } else {
      setMainHesData((prev) => {
        return prev.map((data, index) =>
          index === openAccordianIndex
            ? {
                ...data,
                teamMember: [
                  ...data.teamMember,
                  {
                    name: values.evaluationName,
                    memberName:
                      employeeList.find(
                        (emp) => emp.id === values.evaluationName,
                      )?.name ?? '',
                    discipline:
                      employeeList
                        .find((emp) => emp.id === values.evaluationName)
                        ?.disciplineHistoryList?.find(
                          (h) =>
                            h.disciplineType === values.evaluationDecipline,
                        )?.disciplineType ?? '',
                  },
                ],
              }
            : data,
        );
      });
      setEvaluvationData([
        ...evaluvationData,
        {
          evaluationId:
            employeeList.find((emp) => emp.id === values.evaluationName)?.id ??
            '',
          evaluationName: values.evaluationName,
          evaluationDiscipline:
            employeeList
              .find((emp) => emp.id === values.evaluationName)
              ?.disciplineHistoryList?.find(
                (history) =>
                  history.disciplineType === values.evaluationDecipline,
              )?.disciplineType ?? '',
        },
      ]);
    }
    setNewEvaluvatedName('');
    setNewEvaluvatedDiscipline('');
    setSelectedEvalName('');
  };

  const editEvaluationData = (data: EvalutaionDataType) => {
    const discipline = employeeList
      .filter((employee) => employee.id === data.evaluationName)
      .flatMap(
        (employee) =>
          employee.disciplineHistoryList?.map((history) => ({
            id: history.disciplineType,
            label:
              lookups?.employeeDiscipline.find(
                (disc) => disc.code === history.disciplineType,
              )?.description ?? '',
          })) || [],
      );

    // make unique
    const uniqueDiscipline = Array.from(
      new Map(discipline.map((d) => [d.id, d])).values(),
    );

    setDisciplineList(uniqueDiscipline);

    setEditEvaluationId(data.evaluationId);
    setSelectedEvalName(data.evaluationName);
    formikRef.current?.setValues({
      evaluationName: data.evaluationName,
      evaluationDecipline: data.evaluationDiscipline,
    });
    setNewEvaluvatedName(data.evaluationName);
    setNewEvaluvatedDiscipline(data.evaluationDiscipline);
    setIsEdit(true);
  };
  const deleteEvaluationData = (id: string) => {
    setDeleteIndexId(id);
    setDeleteWarning(true);
  };

  useEffect(() => {
    getAllEmployeeByName('', 0, 10, [])
      .then((data) => {
        toggleLoader(false);
        setEmployeeList(data);
        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((err) => {
        toggleLoader(false);
        setToastrVariable('error');
        setToastrId('IFSP.failedToGetEmployeeData');
        setToastrDefaultMessage(err);

        return [];
      });

    setMainHesData(ifsp.ifspTeam.forms.healthSummary);
    setEvaluvationData(() => {
      return ifsp.ifspTeam.forms.healthSummary[
        openAccordianIndex
      ].teamMember.map((member) => {
        return {
          evaluationId: member.name,
          evaluationName: member.name,
          evaluationDiscipline: member.discipline,
        };
      });
    });
    setHESServiceQuestion((prev) => {
      const tempData = [...prev];
      tempData[0].comments =
        ifsp.ifspTeam.forms.healthSummary[openAccordianIndex].evaluationResults;
      tempData[1].comments =
        ifsp.ifspTeam.forms.healthSummary[openAccordianIndex].healthHistory;
      tempData[2].comments =
        ifsp.ifspTeam.forms.healthSummary[
          openAccordianIndex
        ].currentHealthStatus;
      tempData[3].comments =
        ifsp.ifspTeam.forms.healthSummary[openAccordianIndex].additionalInfo;
      return tempData;
    });
  }, [openAccordianIndex]);

  useEffect(() => {
    updateIfspContext({
      ...ifsp,
      ifspTeam: {
        ...ifsp.ifspTeam,
        forms: {
          ...ifsp.ifspTeam.forms,
          healthSummary: mainHesData,
        },
      },
    });
  }, [mainHesData]);

  useEffect(() => {
    if (close) setOpenAccordian(false);
  }, [close]);

  const createNewSection = () => {
    updateIfspContext({
      ...ifsp,
      ifspTeam: {
        ...ifsp.ifspTeam,
        forms: {
          ...ifsp.ifspTeam.forms,
          healthSummary: [
            ...ifsp.ifspTeam.forms.healthSummary,
            {
              teamMember: [],
              evaluationResults: '',
              healthHistory: '',
              currentHealthStatus: '',
              additionalInfo: '',
              sessionReviewedDate: '',
              dateOfEvaluation: '',
            },
          ],
        },
      },
    });
    setMainHesData([
      ...ifsp.ifspTeam.forms.healthSummary,
      {
        teamMember: [],
        evaluationResults: '',
        healthHistory: '',
        currentHealthStatus: '',
        additionalInfo: '',
        sessionReviewedDate: '',
        dateOfEvaluation: '',
      },
    ]);
    const updatedInitialData = {
      ...initialIfspForReports,
      ifspTeam: {
        ...initialIfspForReports.ifspTeam,
        forms: {
          ...initialIfspForReports.ifspTeam.forms,
          healthSummary: [
            ...initialIfspForReports.ifspTeam.forms.healthSummary,
            {
              teamMember: [],
              evaluationResults: '',
              healthHistory: '',
              currentHealthStatus: '',
              additionalInfo: '',
              sessionReviewedDate: '',
              dateOfEvaluation: '',
            },
          ],
        },
      },
    };
    updateIfspForReport(updatedInitialData);
  };
  const deleteSection = () => {
    updateIfspContext({
      ...ifsp,
      ifspTeam: {
        ...ifsp.ifspTeam,
        forms: {
          ...ifsp.ifspTeam.forms,
          healthSummary: ifsp.ifspTeam.forms.healthSummary.slice(0, -1),
        },
      },
    });
    updateIfspForReport({
      ...initialIfspForReports,
      ifspTeam: {
        ...initialIfspForReports.ifspTeam,
        forms: {
          ...initialIfspForReports.ifspTeam.forms,
          healthSummary:
            initialIfspForReports.ifspTeam.forms.healthSummary.slice(0, -1),
        },
      },
    });
    setMainHesData(ifsp.ifspTeam.forms.healthSummary.slice(0, -1));
    setDeleteNewSection(false);
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const canShowDeleteHealthSummary = (mainIndex: number) =>
    permission &&
    !ifsp.ifspTeam.forms.healthSummary[
      ifsp.ifspTeam.forms.healthSummary.length - 1
    ]?.sessionReviewedDate &&
    ifsp.ifspTeam.forms.healthSummary.length - 1 === mainIndex &&
    mainIndex > 0;

  const deleteHealthSummaryNode = (mainIndex: number) =>
    canShowDeleteHealthSummary(mainIndex) ? (
      <Box
        component="div"
        className="cursorPointer flex__ align__items__center pr-md"
        onClick={() => {
          setDeleteNewSection(true);
        }}
        sx={{
          padding: '4px 8px 4px 8px',
          borderRadius: '4px',
          marginRight: '24px',
          border: '1px solid #EB4C60',
          backgroundColor: '#fff',
        }}
      >
        <DeleteIcon className="pr-xs" />
        <MediumTypography
          labelid="IFSP.DeleteNewSection"
          defaultlabel="Delete"
          fontSize="12px"
          fontweight={600}
        />
      </Box>
    ) : null;

  return (
    <Formik
      initialValues={{
        evaluationName: newEvaluvatedName,
        evaluationDecipline: newEvaluvatedDiscipline,
      }}
      innerRef={formikRef}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        resetForm();
        setDisciplineList([]);
        addEvaluationdata(values);
      }}
    >
      {({
        values,
        setFieldValue,
        setValues,
        setErrors,
        errors,
        touched,
        handleSubmit,
      }) => (
        <Box component="main">
          {info && (
            <InformationModal
              open={info}
              titleLabel="IFSP.Information"
              titleDefaultLabel="Information"
              buttonLabel="IFSP.InfoOkButton"
              buttonDefaultLabel="Ok"
              onClose={() => {
                closeInfo(info);
              }}
              info={HealthEligibilitySummaryData.info}
            />
          )}
          <SnackBarComponent
            open={open}
            handleClose={handleCloseSnakBar}
            successOrError={toastrVariable}
            labelId={toastrId}
            defaultMessageId={toastrDefaultMessage}
          />
          <ModalPopup
            open={duplicateEvaluationDisciplineError}
            description={'duplicateEvalDisciplineWarningMessage'}
            onOk={() => {
              setSelectedEvalName('');
              setDuplicateEvaluationDisciplineError(false);
            }}
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="Ok"
          />
          <ModalPopup
            open={deleteWarning}
            description="deleteConfirmationMessage"
            onOk={() => {
              const newEvaluationData = evaluvationData.filter(
                (evaluation) => evaluation.evaluationId !== deleteIndexId,
              );
              setMainHesData((prev) => {
                const tempData = [...prev];
                tempData[openAccordianIndex].teamMember = newEvaluationData.map(
                  (evaluation) => ({
                    name: evaluation.evaluationId,
                    memberName:
                      employeeList.find(
                        (emp) => emp.id === evaluation.evaluationId,
                      )?.name ?? '',
                    discipline: evaluation.evaluationDiscipline,
                  }),
                );
                return tempData;
              });
              setEvaluvationData(newEvaluationData);
              setDeleteWarning(false);
            }}
            onCancel={() => {
              setDeleteWarning(false);
            }}
            labelId1="Clientpage.cancelbtn"
            negativeActionLabel="cancelText"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="Ok"
          />

          <ModalPopup
            open={deleteNewSection}
            description="deleteConfirmationMessage"
            onOk={() => {
              deleteSection();
            }}
            onCancel={() => {
              setDeleteNewSection(false);
            }}
            labelId1="Clientpage.cancelbtn"
            negativeActionLabel="cancelText"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="Ok"
          />
          {ifsp.ifspTeam.forms.healthSummary.map((ifspData, mainIndex) => (
            <Box
              key={mainIndex + '1'}
              component="section"
              className="p-lg borderRadius4 mb-lg"
              sx={{
                border: '1px solid #00C6B8',
                backgroundColor: '#ECF9F8',
              }}
            >
              <Box component="div" className="flex__justify__space-between">
                {openAccordian && mainIndex === openAccordianIndex ? (
                  <Box component="div" className="flex__justify__space-between">
                    <MediumTypography
                      labelid="IFSP.session2"
                      defaultlabel="Health & Eligibility Summary"
                      fontSize="18px"
                      fontweight={600}
                      textColor="#2A4241"
                      className="pr-md"
                    />
                    <InfoIcon
                      style={{ alignSelf: 'center' }}
                      className="cursorPointer"
                      onClick={() => {
                        openInfo(info);
                      }}
                    />
                    {ifsp.ifspTeam.forms.healthSummary[mainIndex]
                      .sessionReviewedDate ? (
                      <Box
                        sx={{
                          display: 'flex',
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginLeft: '24px',
                          backgroundColor: '#97A6A5',
                          alignItems: 'center',
                        }}
                      >
                        <MediumTypography
                          labelid="IFSP.UpdatedOn"
                          defaultlabel="Created On: "
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                        />
                        <MediumTypography
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                          className="pl-xs"
                          label={ifspData.sessionReviewedDate as string}
                        />
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box className="flex__justify__space-between">
                    <MediumTypography
                      labelid="IFSP.session2"
                      defaultlabel="Health & Eligibility Summary"
                      fontSize="18px"
                      fontweight={600}
                      textColor="#2A4241"
                    />
                    {ifsp.ifspTeam.forms.healthSummary[mainIndex]
                      .sessionReviewedDate ? (
                      <Box
                        sx={{
                          display: 'flex',
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginLeft: '24px',
                          backgroundColor: '#97A6A5',
                          alignItems: 'center',
                        }}
                      >
                        <MediumTypography
                          labelid="IFSP.UpdatedOn"
                          defaultlabel="Created On: "
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                        />
                        <MediumTypography
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                          className="pl-xs"
                          label={ifspData.sessionReviewedDate as string}
                        />
                      </Box>
                    ) : null}
                  </Box>
                )}
                {openAccordian && mainIndex === openAccordianIndex ? (
                  <Box className="flex__ align__items__center">
                    {permission &&
                    ifsp.ifspTeam.forms.healthSummary[
                      ifsp.ifspTeam.forms.healthSummary.length - 1
                    ].sessionReviewedDate &&
                    ifsp.ifspTeam.forms.healthSummary.length - 1 ===
                      mainIndex ? (
                      <Box
                        className="cursorPointer"
                        sx={{
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginRight: '24px',
                          border: '1px solid #008C82',
                          backgroundColor: '#fff',
                          pointerEvents: permission ? 'auto' : 'none',
                          opacity: permission ? 'unset' : '0.8',
                        }}
                        onClick={() => {
                          createNewSection();
                        }}
                      >
                        <MediumTypography
                          labelid="IFSP.AddNewSection"
                          defaultlabel="+ Add New"
                          fontSize="12px"
                          fontweight={600}
                        />
                      </Box>
                    ) : (
                      deleteHealthSummaryNode(mainIndex)
                    )}
                    <UpArrow
                      className="cursorPointer"
                      onClick={() => {
                        formikRef.current?.setTouched({}, false);
                        setOpenAccordianIndex((prev) => prev);
                        setOpenAccordian(false);
                      }}
                    />
                  </Box>
                ) : (
                  <Box className="flex__ align__items__center">
                    {permission &&
                    ifsp.ifspTeam.forms.healthSummary[
                      ifsp.ifspTeam.forms.healthSummary.length - 1
                    ].sessionReviewedDate &&
                    ifsp.ifspTeam.forms.healthSummary.length - 1 ===
                      mainIndex ? (
                      <Box
                        className="cursorPointer"
                        sx={{
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginRight: '24px',
                          border: '1px solid #008C82',
                          backgroundColor: '#fff',
                          pointerEvents: permission ? 'auto' : 'none',
                          opacity: permission ? 'unset' : '0.8',
                        }}
                        onClick={() => {
                          createNewSection();
                        }}
                      >
                        <MediumTypography
                          labelid="IFSP.AddNewSection"
                          defaultlabel="+ Add New"
                          fontSize="12px"
                          fontweight={600}
                        />
                      </Box>
                    ) : (
                      deleteHealthSummaryNode(mainIndex)
                    )}
                    <DownArrow
                      className="cursorPointer"
                      onClick={() => {
                        formikRef.current?.setTouched({}, false);
                        setOpenAccordianIndex(mainIndex);
                        setOpenAccordian(true);
                        handelClose(false);
                      }}
                    />
                  </Box>
                )}
              </Box>
              {openAccordian && mainIndex === openAccordianIndex && (
                <Box component="div" className="mt-lg">
                  <Box
                    ref={parentRef}
                    style={{
                      pointerEvents: permission ? 'auto' : 'none',
                      opacity: permission ? 'unset' : '0.8',
                    }}
                  >
                    <Grid container xs={12}>
                      <Grid item xs={4.5}>
                        <DatePickerComponent
                          className="bg__white"
                          labelid="IFSP.eligibilityEvaluationDate"
                          disableFuture={false}
                          defaultlabelid="Date of Eligibility Evaluation"
                          value={
                            mainHesData[openAccordianIndex].dateOfEvaluation ===
                              '' || null
                              ? null
                              : dayjs(
                                  mainHesData[openAccordianIndex]
                                    .dateOfEvaluation,
                                )
                          }
                          handlechange={(dateValue: Dayjs | null) => {
                            if (dateValue !== null) {
                              const formattedDate =
                                dayjs(dateValue).format('MM/DD/YYYY');
                              setMainHesData((prev) =>
                                prev.map((item, index) => {
                                  if (index === openAccordianIndex) {
                                    return {
                                      ...item,
                                      dateOfEvaluation: formattedDate,
                                    };
                                  }
                                  return item;
                                }),
                              );
                            } else {
                              setMainHesData((prev) =>
                                prev.map((item, index) => {
                                  if (index === openAccordianIndex) {
                                    return {
                                      ...item,
                                      dateOfEvaluation: null,
                                    };
                                  }
                                  return item;
                                }),
                              );
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container className="mt-lg">
                      <Grid item xs={4.5} className="pr-lg" display={'none'}>
                        <AutocompleteSearch
                          className="bg__white"
                          labelId="IFSP.evaluationTeamMemberName"
                          defaultId="Evaluation Team Member Name"
                          Required
                          data={employeeList
                            .filter(
                              (employee) =>
                                !ifsp.ifspTeam.forms.healthSummary[
                                  openAccordianIndex
                                ].teamMember.some(
                                  (teamMember) =>
                                    teamMember.name === employee.id,
                                ),
                            )
                            .map((employee) => ({
                              id: employee.id ?? '',
                              label: employee.name
                                ? employee.lastName + ', ' + employee.firstName
                                : '',
                            }))}
                          selectedValue={{
                            id:
                              employeeList.find(
                                (employee) =>
                                  employee.id === values.evaluationName,
                              )?.id ?? '',
                            label: formatName(
                              employeeList.find(
                                (employee) =>
                                  employee.id === values.evaluationName,
                              )?.firstName ?? '',
                              null,
                              employeeList.find(
                                (employee) =>
                                  employee.id === values.evaluationName,
                              )?.lastName ?? '',
                              null,
                            ),
                          }}
                          onSelectItem={(data) => {
                            if (data) {
                              setFieldValue('evaluationName', data.id);
                              const discipline = employeeList
                                .filter((employee) => employee.id === data.id)
                                .map((employee) => ({
                                  id: employee.id ?? '',
                                  label:
                                    lookups?.employeeDiscipline.find(
                                      (dis) =>
                                        dis.code === employee.disciplineType,
                                    )?.description ?? '',
                                }));
                              if (discipline.length > 0) {
                                setValues({
                                  evaluationName: data.id,
                                  evaluationDecipline: '',
                                });
                                setErrors({
                                  evaluationName: errors.evaluationName,
                                  evaluationDecipline:
                                    'IFSP.EvaluationDecipline',
                                });
                                setDisciplineList(discipline);
                              } else {
                                setDisciplineList([]);
                                setValues({
                                  evaluationName: '',
                                  evaluationDecipline: '',
                                });
                                setErrors({
                                  evaluationName: 'IFSP.EvaluationName',
                                  evaluationDecipline:
                                    'IFSP.EvaluationDecipline',
                                });
                              }
                            } else {
                              setDisciplineList([]);
                              setValues({
                                evaluationName: '',
                                evaluationDecipline: '',
                              });
                              setErrors({
                                evaluationName: 'IFSP.EvaluationName',
                                evaluationDecipline: 'IFSP.EvaluationDecipline',
                              });
                            }
                          }}
                          disableLabel={false}
                        />
                        {errors.evaluationName && touched.evaluationName && (
                          <MediumTypography
                            className="errorText-md"
                            labelid={errors.evaluationName}
                            defaultlabel="Evaluation team member is required"
                          />
                        )}
                      </Grid>
                      <Grid item xs={4.5} className="pr-lg">
                        <LazyAutocompleteSearch
                          labelId="IFSP.evaluationTeamMemberName"
                          defaultId="Evaluation Team Member Name"
                          value={evaluationMember}
                          onChange={(data) => {
                            if (data) {
                              setSelectedEvalName(data.id);
                              setFieldValue('evaluationName', data.id);
                              const discipline = employeeList
                                .filter((employee) => employee.id === data.id)
                                .flatMap(
                                  (employee) =>
                                    employee.disciplineHistoryList?.map(
                                      (history) => ({
                                        id: history.disciplineType ?? '',
                                        label:
                                          lookups?.employeeDiscipline.find(
                                            (dis) =>
                                              dis.code ===
                                              history.disciplineType,
                                          )?.description ?? '',
                                      }),
                                    ) || [],
                                );

                              // unique disciplines
                              const uniqueDiscipline = Array.from(
                                new Map(
                                  discipline.map((d) => [d.id, d]),
                                ).values(),
                              );
                              if (uniqueDiscipline.length > 0) {
                                setDisciplineList(uniqueDiscipline);
                              } else {
                                setDisciplineList([]);
                              }
                            } else {
                              setSelectedEvalName('');
                              setDisciplineList([]);
                              setValues({
                                evaluationName: '',
                                evaluationDecipline: '',
                              });
                              setErrors({
                                evaluationName: 'IFSP.EvaluationName',
                                evaluationDecipline: 'IFSP.EvaluationDecipline',
                              });
                            }
                          }}
                          fetchOptions={fetchEmployees}
                          required
                        />
                        {errors.evaluationName && touched.evaluationName && (
                          <MediumTypography
                            className="errorText-md"
                            labelid={errors.evaluationName}
                            defaultlabel="Evaluation team member is required"
                          />
                        )}
                      </Grid>
                      <Grid item xs={4.5} className="align__items__center">
                        <AutocompleteSearch
                          className="bg__white"
                          labelId="IFSP.evaluationTeamMemberDiscipline"
                          defaultId="Evaluation Team Member Discipline"
                          Required
                          data={disciplineList.filter(
                            (discipline) => discipline.id && discipline.label,
                          )}
                          selectedValue={{
                            id:
                              disciplineList.find(
                                (discipline) =>
                                  discipline.id === values.evaluationDecipline,
                              )?.id ?? '',
                            label:
                              disciplineList.find(
                                (discipline) =>
                                  discipline.id === values.evaluationDecipline,
                              )?.label ?? '',
                          }}
                          onSelectItem={(data) => {
                            if (data) {
                              setFieldValue('evaluationDecipline', data.id);
                            } else {
                              setFieldValue('evaluationDecipline', '');
                            }
                          }}
                          disableLabel={false}
                        />
                        {errors.evaluationDecipline &&
                          touched.evaluationDecipline && (
                            <MediumTypography
                              className="errorText-md"
                              labelid={errors.evaluationDecipline}
                              defaultlabel="Evaluation team member discipline is required"
                            />
                          )}
                      </Grid>
                      <Grid item xs={2}>
                        <Box
                          component="div"
                          className="cursorPointer flex__ ml-lg mt-xs"
                          onClick={() => handleSubmit()}
                        >
                          <AddIcon className="mr-sm" />
                          <MediumTypography
                            labelid="IFSP.Add"
                            defaultlabel="Add"
                            fontweight={400}
                            textColor="#2A4241"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Box component="div" className="mt-md">
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHeader
                            className="listDataTableHead"
                            headerNames={headCells}
                            checkBoxRequired={false}
                          />
                          <TableBody className="tableRowcss">
                            {evaluvationData.map(
                              (listData: EvalutaionDataType) => (
                                <EvaluationTableRow
                                  key={listData.evaluationId}
                                  employeeList={employeeList}
                                  data={listData}
                                  onEdit={(data) => {
                                    editEvaluationData(data);
                                  }}
                                  permission={permission}
                                  onDelete={(data) => {
                                    deleteEvaluationData(data);
                                  }}
                                />
                              ),
                            )}
                          </TableBody>
                        </Table>
                        {evaluvationData.length === 0 && (
                          <Box component="div" className=" width__100">
                            <MediumTypography
                              textstyle={'center'}
                              labelid="IFSP.emptyMessage"
                              defaultlabel="No data added yet"
                              className="p-sm"
                            />
                          </Box>
                        )}
                      </TableContainer>
                    </Box>
                    <Box component="div" className="mt-sm">
                      <SimpleTextAndTextArea
                        placeholder="Enter comments here"
                        questions={HealthEligibilitySummaryData.Questions}
                        rows={5}
                        customCommentStyles={{
                          backgroundColor: '#ffffff',
                          minWidth: '840px',
                          width: '840px',
                        }}
                        maxLength={5000}
                        Value={hesServiceQuestion}
                        handlechange={(value: {
                          commentId: number;
                          comments: string;
                        }) => {
                          const updatedComments = [...hesServiceQuestion];
                          updatedComments[value.commentId].comments =
                            value.comments;
                          setHESServiceQuestion((prev) =>
                            prev.map((item, index) => {
                              if (index === openAccordianIndex) {
                                return {
                                  ...item,
                                  evaluationResults:
                                    updatedComments[0].comments,
                                  healthHistory: updatedComments[1].comments,
                                  currentHealthStatus:
                                    updatedComments[2].comments,
                                  additionalInfo: updatedComments[3].comments,
                                };
                              }
                              return item;
                            }),
                          );

                          const updatedData = {
                            ...mainHesData,
                            evaluationResults: updatedComments[0].comments,
                            healthHistory: updatedComments[1].comments,
                            currentHealthStatus: updatedComments[2].comments,
                            additionalInfo: updatedComments[3].comments,
                          };
                          setMainHesData((prev) =>
                            prev.map((item, index) => {
                              if (index === openAccordianIndex) {
                                return {
                                  ...item,
                                  evaluationResults:
                                    updatedData.evaluationResults,
                                  healthHistory: updatedData.healthHistory,
                                  currentHealthStatus:
                                    updatedData.currentHealthStatus,
                                  additionalInfo: updatedData.additionalInfo,
                                };
                              }
                              return item;
                            }),
                          );
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Formik>
  );
};

export default HealthEligibilitySummary;
